/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { produce } from 'immer';
import cloneDeep from 'lodash/cloneDeep';

import { Key } from 'react';
import { queryClient } from 'src/QueryClient';
import { GlobalApi, PolicyApi, UserApi } from '@Apis';
import i18nInst from 'src/i18n';
import {
  getAtpPolicyConfig,
  getExchangeTemplateConfig,
} from 'src/pages/Policy/TemplateConfig/cfg.exchange';
import {
  checkIfPolicyHasEnabledFilter,
  convertToTargets,
  convertUserGroup,
  getAllTargetsDisplay,
  getPolicyName,
  handleAllTargetsForTeams,
  handleValidateError,
  unCheckTargetGroup,
} from 'src/pages/Policy/utils/fns';
import {
  ActionData,
  ActionEnum,
  AntiSpamFilter,
  ApplyBoundEnum,
  ApprovedHeader,
  AsActionKeys,
  AsNotifyTypeKeys,
  AsSuspiciousTagKeys,
  DdaActionKeys,
  DdaFilter,
  DdaNotifyTypeKeys,
  DdaSuspiciousTagKeys,
  DlpPolicyFilters,
  DlpPolicyItem,
  DlpTemplate,
  DlpTemplateItem,
  DpFilter,
  EnableDisableEnum,
  EnableDisableStatus,
  ExchangeOnlinePolicyItem,
  FileBlockingFilter,
  Filter,
  KeywordExtractionFilter,
  KfContainRule,
  MergedRiskAiFilterRule,
  MipLabelItem,
  Notification,
  PolicyFilters,
  PolicyHolderTypeEnum,
  RecipientGroup,
  RiskAiFilter,
  RiskAiFilterRule,
  ShareLinkFilter,
  TargetCapacity,
  TargetGroup,
  Targets,
  VirusScanFilter,
  VsActionKeys,
  VsNotifyTypeKeys,
  WebThreatProtectionFilter,
} from '@Policy/utils/types';
import { TargetType } from 'src/utils/enums';
import { createStore } from 'zustand';
import { devtools } from 'zustand/middleware';

import { reportPendo } from '../../../utils/setupPendo';
import { TreeNode } from 'src/components/Tree/type';
// import { createGenralInlineSlice } from '../../../../tempFiles/ExchangeOnlineInbound/General/store';
import {
  GmailInlinePolicyEvent,
  PolicyServiceMap,
  PolicyTypeEnum,
} from '@Policy/utils/constants';
import {
  AdvancedFeatureInfo,
  PolicyType,
  PolicyType2Service,
  Service,
} from '@Types/app';
// import { createGeneralTeamsSlice } from '../../../../tempFiles/Collaboration/Teams/General/store';
// import { createGeneralGmailInlineSlice } from '../../../../tempFiles/GmailInlineInbound/General/store';
import { createAntiSpamSlice } from './filters/advancedSpamProtection';
import { createVirusScanSlice } from './filters/malwareScanning';
import { createFileBlockingSlice } from './filters/fileBlocking';
import { createWebThreatProtectionSlice } from './filters/webReputation';
import { createDdaSlice } from './filters/virtualAnalyzer';
import { createGeneralSlice } from './filters/general/store';
import { createDlpFilterSlice } from './filters/dlp';
import { createKeywordExtractionFilterSlice } from './filters/keywordExtraction';
import { createShareLinkFilterSlice } from './filters/shareLink';
import { createRiskAiFilterSlice } from './filters/riskAI/createStore';
import { BlockedHeaderRuleDefinitionsProps } from '@Components/BlockedHeaderFieldList';
import { CorrelatedRuleShallow } from 'src/pages/GlobalSetting/CorrelatedRuleTemplates/types';
import { UserTeamIcon } from '@tonic-one/react-icons';
import { PolicySubTypeEnum } from '@Types/index';

// General slice type
interface GeneralActions {
  handleToRightTargets: (keys: Key[]) => void;
  handleDeleteTarget: (key: Key) => void;
  setGeneralState: (payload: Partial<ExchangeOnlinePolicyItem>) => void;
  validateGeneral: () => any;
  setActiveTabIndex: (index: number) => void;
  // for inline
  handleToRightTargetsInline: (keys: Key[]) => void;
  handleDeleteTargetInline: (key: Key) => void;
  handleToRightTargetDomains: (keys: Key[]) => void;
  handleDeleteTargetDomain: (key: Key) => void;
  validateGeneralInline: () => any;
  // for teams
  handleToRightTargetTeams: (keys: Key[]) => void;
  handleDeleteTargetTeams: (key: Key) => void;
  // for gmail inline
  handleToRightTargetsGmailInline: (keys: Key[]) => void;
  handleDeleteTargetGmailInline: (key: Key) => void;
  // for salesforce profiles
  handleToRightTargetApps: (keys: Key[]) => void;
  handleDeleteTargetApps: (key: Key) => void;
  handleToRightTargetProfiles: (keys: Key[]) => void;
  handleDeleteTargetProfiles: (key: Key) => void;
  // for dlp salesforce
  handleToRightTargetObjects: (keys: Key[]) => void;
  handleDeleteTargetObjects: (key: Key) => void;
  validateGeneralSalesforce: () => any;
  validateDlpSalesforce: () => any;
}

// AntispamFilter slice type
interface AntispamFilterActions {
  setAsState: (payload: Partial<AntiSpamFilter>) => void;
  setAsNtfState: (payload: Partial<Notification>) => void;
  asActionHandler: (key: AsActionKeys) => (action: ActionEnum) => void;
  asNotifyTypeHandler: (key: AsNotifyTypeKeys) => void;
  asSuspiciousTagHandler: (key: AsSuspiciousTagKeys) => (value: string) => void;
  addAsApprovedSender: (email: string | string[]) => void;
  removeAsApprovedSender: (email: string) => void;
  addAsBlockedSender: (email: string | string[]) => void;
  removeAsBlockedSender: (email: string) => void;
  addAsApprovedHeader: (header: ApprovedHeader) => void;
  removeAsApprovedHeader: (header: ApprovedHeader) => void;
  addAsBlockedHeader: (header: BlockedHeaderRuleDefinitionsProps[]) => void;
  removeAsBlockedHeader: (header: BlockedHeaderRuleDefinitionsProps[]) => void;
  importAsApprovedSender: (emails: string[]) => void;
  importAsBlockedSender: (emails: string[]) => void;
  validateAntispam: () => any;
}

interface VirusScanFilterActions {
  setVsState: (payload: Partial<VirusScanFilter>) => void;
  setVsNtfState: (payload: Partial<Notification>) => void;
  setVsEnablePredictiveMachineLearning: (
    vsEnablePredictiveMachineLearning: EnableDisableEnum
  ) => void;
  setVsSpecifiedFileTypeObjects: (selectedKeys: string[]) => void;
  vsActionHandler: (key: VsActionKeys) => (action: ActionEnum) => void;
  vsNotifyTypeHandler: (key: VsNotifyTypeKeys) => void;
  setVsUnscannableFileOptions: (props: {
    replaceFileName: string;
    replaceText: string;
    actionConfig: Partial<ActionData>[];
  }) => void;
  addSpecifiedFileExt: (ext: string) => void;
  removeSpecifiedFileExt: (item: any) => void;
  addVsExceptSenders: (keys: string[]) => void;
  removeVsExceptSenders: (key: string) => void;
  addVsExceptRecipients: (keys: string[]) => void;
  removeVsExceptRecipient: (key: string) => void;
  importVsExceptSenders: (entries: string[]) => void;
  importVsExceptRecipients: (entries: string[]) => void;
  validateVirusScan: () => any;
  setVsApplyBound: (payload: { [key: string]: ApplyBoundEnum }) => void;
}

interface FileBlockingFilterActions {
  setFbState: (payload: Partial<FileBlockingFilter>) => void;
  setFbNtfState: (payload: Partial<Notification>) => void;
  setFbExceptionFileTypeList: (selectedKeys: string[]) => void;
  addExceptionFileExts: (exts: string[] | string) => void;
  removeExceptionFileExt: (ext: string) => void;
  addExceptionFileNames: (exts: string[] | string) => void;
  removeExceptionFileName: (ext: string) => void;
  setFbBlockFileTypeList: (selectedKeys: string[]) => void;
  addBlockFileExts: (exts: string[] | string) => void;
  removeBlockFileExt: (ext: string) => void;
  addBlockFileNames: (exts: string[] | string) => void;
  removeBlockFileName: (ext: string) => void;
  setFbIncomingRuleBlockFileTypeList: (selectedKeys: string[]) => void;
  addAdditionalBlockFileExts: (exts: string[] | string) => void;
  removeAdditionalBlockFileExt: (ext: string) => void;
  addAdditionalBlockFileNames: (exts: string[] | string) => void;
  removeAddtionalBlockFileName: (ext: string) => void;
  addFbApprovedSender: (email: string | string[]) => void;
  removeFbApprovedSender: (email: string) => void;
  setFbActionType: (action: ActionEnum) => void;
  setFbNotifyType: (e: React.ChangeEvent<HTMLInputElement>) => void;
  importFbApprovedSender: (emails: string[]) => void;
  validateFileBlocking: () => any;
}
interface WebThreatProtectionFilterActions {
  setWtpState: (payload: Partial<WebThreatProtectionFilter>) => void;
  setWtpNtfState: (payload: Partial<Notification>) => void;
  addWtpApprovedSender: (email: string | string[]) => void;
  removeWtpApprovedSender: (email: string) => void;
  addWtpApprovedUrls: (url: string | string[]) => void;
  removeWtpApprovedUrl: (url: string) => void;
  addWtpBlockedUrls: (url: string | string[]) => void;
  removeWtpBlockedUrl: (url: string) => void;
  addWtpApprovedHeader: (header: ApprovedHeader) => void;
  removeWtpApprovedHeader: (header: ApprovedHeader) => void;
  importWtpApprovedUrls: (url: string[]) => void;
  importWtpBlockedUrls: (url: string[]) => void;
  importWtpApprvoedSender: (emails: string[]) => void;
  validateWebReputation: () => any;
  addWtpTocApprovedUrls: (url: string | string[]) => void;
  removeWtpTocApprovedUrls: (url: string) => void;
  importWtpTocApprovedUrls: (url: string[]) => void;
}

interface DdaFilterActions {
  setDdaState: (payload: Partial<DdaFilter>) => void;
  setDdaNtfState: (payload: Partial<Notification>) => void;
  addDdaApprovedSender: (email: string | string[]) => void;
  removeDdaApprovedSender: (email: string) => void;
  addDdaApprovedFile: (file: string | string[], limit?: number) => void;
  removeDdaApprovedFile: (file: string) => void;
  ddaActionHandler: (key: DdaActionKeys) => (action: ActionEnum) => void;
  ddaNotifyTypeHandler: (key: DdaNotifyTypeKeys) => void;
  ddaSuspiciousTagHandler: (
    key: DdaSuspiciousTagKeys
  ) => (value: string) => void;
  setDdaTempQuarantineEnabled: (
    ddaTempQuarantineEnabled: EnableDisableStatus
  ) => void;
  setDdaUrlEnabled: (ddaUrlEnabled: EnableDisableStatus) => void;
  importDdaApprovedSender: (emails: string[]) => void;
  importDdaApprovedFile: (files: string[]) => void;
  validateVirtualAnalyzer: () => any;
  setDdaApplyBound: (payload: { [key: string]: ApplyBoundEnum }) => void;
  checkAdvancedFeature: () => Promise<AdvancedFeatureInfo | undefined>;
}

interface DlpFilterActions {
  setDpState: (payload: Partial<DpFilter>) => void;
  setDpNtfState: (payload: Partial<Notification>) => void;
  setDlpTemplateAction: (templateId: string, action: ActionEnum) => void;
  setMipLabelAction: (labelId: string, action: ActionEnum) => void;
  addDlpTemplates: (templates: DlpTemplateItem[]) => void;
  addDlpTemplatesAndMipLabels: (
    templates: (DlpTemplateItem | MipLabelItem)[]
  ) => void;
  deleteDlpTemplate: (templateId: string) => void;
  deleteMipLabel: (labelId: string) => void;
  syncLabels: () => Promise<void>;
  validateDlp: () => any;
  addDlpApprovedSender: (email: string | string[]) => void;
  removeDlpApprovedSender: (email: string) => void;
  importDlpApprovedSender: (emails: string[]) => void;
}

interface RiskAiFilterActions {
  setRiskAiState: (payload: Partial<RiskAiFilter>) => void;
  setRiskAiNtfState: (payload: Partial<Notification>) => void;
  validateRiskAi: () => any;
  setRiskAiRuleAction: (ruleId: string, action: ActionEnum) => void;
  createMergedRiskAiFilterRule: (
    rule: CorrelatedRuleShallow,
    data?: RiskAiFilterRule
  ) => MergedRiskAiFilterRule;
  addRiskAiApprovedSender: (email: string | string[]) => void;
  removeRiskAiApprovedSender: (email: string) => void;
  importRiskAiApprovedSender: (emails: string[]) => void;
}

interface KeywordExtractionFilterActions {
  setKfState: (payload: Partial<KeywordExtractionFilter>) => void;
  setKfNtfState: (payload: Partial<Notification>) => void;
  addKfContainRule: (rules: KfContainRule[]) => void;
  deleteKfContainRule: (index: number) => void;
  setKfContainRuleAction: (index: number, action: ActionEnum) => void;
  validateKeywordExtraction: () => any;
}

interface ShareLinkFilterActions {
  setSlfState: (payload: Partial<ShareLinkFilter>) => void;
  setSlfNtfState: (payload: Partial<Notification>) => void;
  addPathToExceptionPaths: (path: string) => void;
  removePathFromExceptionPaths: (path: string) => void;
  setException: (index: number) => void;
  deleteException: (index: number) => void;
  setEditExceptionStatus: (index: number) => void;
  validateShareLink: () => any;
}

export type PolicyTemplateProps = ExchangeOnlinePolicyItem | DlpPolicyItem;
export interface PolicyConfig {
  [key: string]: any;
}
export interface PolicyBasic {
  template: PolicyTemplateProps;
  initTemplate: PolicyTemplateProps;
  config: PolicyConfig;
}
export interface PolicyState extends PolicyBasic {
  availableTargets: any;
  availableTargetsGerneral: TreeNode[];
  availableTargetsExtra: TreeNode[];
  orgId: string;
  recipientGroups: RecipientGroup[];
  activeTabIndex: number;
  tempVars: {
    targetType: TargetType;
    targetUserGroups: Targets;
    targetDomains: Targets;
    targetObjects: Targets;
    targetCommunities: Targets;
    targetProfiles: Targets;
    searchedTargets: TreeNode[];
    [key: string]: any;
  };
  // for inline domain
  availableTargetDomains: any;
  // for dlp
  dlpTemplates: DlpTemplate[];
  mipLabels: MipLabelItem[];
  // for salesforce
  availableTargetObjects: any;
  availableTargetCommunities: any;
  availableTargetProfiles: any;
  availableTargetObjectsGeneral: TreeNode[];
  availableTargetCommunitiesGeneral: TreeNode[];
  availableTargetProfilesGeneral: TreeNode[];
}
export interface PolicyActions {
  getAllTargetsBase: (
    initial?: boolean,
    serviceType?: Service,
    targetType?: TargetType,
    isSalesforceProfiles?: boolean
  ) => Promise<void>;
  getAllTargetsByService: (initial?: boolean) => Promise<void>;
  getAllHpuTargetsByService: (initial?: boolean) => Promise<void>;
  getAllLocalAccountsTargets: (initial?: boolean) => Promise<void>;
  getAllObjectsTargetsByService: (
    initial?: boolean,
    serviceType?: Service
  ) => Promise<void>;
  getAllCommunitiesTargetsByService: (
    initial?: boolean,
    serviceType?: Service
  ) => Promise<void>;
  getAllProfilesTargetsByService: (
    initial?: boolean,
    serviceType?: Service
  ) => Promise<void>;
  getSpecificTargetBasic: ({
    targetIds,
    serviceType,
    targetType,
    pagination,
    category,
  }: {
    targetIds: string[];
    serviceType: Service;
    targetType: TargetType;
    pagination?: Targets['pagination'];
    category?: PolicyHolderTypeEnum;
  }) => Promise<void>;
  getSpecificTargetByService: (
    targetIds: string[],
    pagination?: Targets['pagination']
  ) => Promise<void>;
  getSpecificHpuTargetByService: (
    targetIds: string[],
    pagination?: Targets['pagination']
  ) => Promise<void>;
  getSpecificLocalAccountsTargets: (
    targetIds: string[],
    pagination?: Targets['pagination']
  ) => Promise<void>;
  getSpecificObjectTargetByService: (
    targetIds: string[],
    pagination?: Targets['pagination']
  ) => Promise<void>;
  getSpecificCommunityTargetByService: (
    targetIds: string[],
    pagination?: Targets['pagination']
  ) => Promise<void>;
  getSpecificProfileTargetByService: (
    targetIds: string[],
    pagination?: Targets['pagination']
  ) => Promise<void>;
  getRecipientGroups: () => Promise<void>;
  getValidateArr: () => (() => any)[];
  validateAll: () => boolean;
  submitPolicy: (props: {
    successCallback: () => void;
    policyId?: string;
  }) => Promise<void> | void;
  setTempVars: (payload: { [key: string]: any }) => void;
  getAllTargetDomainsBase: (
    initial: boolean,
    serviceType: string
  ) => Promise<void>;
  getAllTargetDomainsByService: (initial?: boolean) => Promise<void>;
  searchTargetBase: (
    keyword: string,
    targetType?: TargetType,
    pagination?: Targets['pagination'],
    category?: number
  ) => Promise<TreeNode[]>;
  searchHpuTargetByService: (
    keyword: string,
    targetType?: TargetType,
    pagination?: Targets['pagination']
  ) => Promise<TreeNode[]>;
  searchLocalAccountTargetsBase: (
    keyword: string,
    pagination?: Targets['pagination']
  ) => Promise<TreeNode[]>;
  searchObjectsTargetByService: (
    keyword: string,
    targetType?: TargetType,
    pagination?: Targets['pagination']
  ) => Promise<TreeNode[]>;
  searchCommunitiesTargetByService: (
    keyword: string,
    targetType?: TargetType,
    pagination?: Targets['pagination']
  ) => Promise<TreeNode[]>;
  searchProfilesTargetByService: (
    keyword: string,
    targetType?: TargetType,
    pagination?: Targets['pagination']
  ) => Promise<TreeNode[]>;
  syncUsersByService: () => Promise<void>;
  syncDomainsByService: () => Promise<void>;
  syncObjectsByService: () => Promise<void>;
  syncProfilesByService: () => Promise<void>;
  syncCommunitiesByService: () => Promise<void>;
  // for dlp
  getDlpTemplates: () => Promise<void>;
  getMipLabels: () => Promise<void>;
  validateDlp: () => any;
}
// store inclue all slice store type
export type PolicyStore = PolicyState &
  PolicyActions &
  GeneralActions &
  AntispamFilterActions &
  VirusScanFilterActions &
  FileBlockingFilterActions &
  WebThreatProtectionFilterActions &
  DdaFilterActions &
  DlpFilterActions &
  KeywordExtractionFilterActions &
  ShareLinkFilterActions &
  RiskAiFilterActions;

const getDefaultProps = () => {
  const { t } = i18nInst;
  const accountEmail = undefined;
  const config = getExchangeTemplateConfig(t, {}, accountEmail);
  return config;
};
const getDefaultConfig = () => {
  const { t } = i18nInst;
  const currentOrgSetting = {};
  const config = getAtpPolicyConfig(t, currentOrgSetting, getDefaultProps());
  return config;
};

const setRecipientGroupInPolicy = <
  P extends PolicyTemplateProps,
  F extends Filter<P>,
>(
  policy: P,
  policyFilter: F,
  recipientGroups: RecipientGroup[],
  setPolicy: Function
) => {
  const { policyUuid } = policy;
  const defaultGroup = recipientGroups.find(
    (group: RecipientGroup) => group.isDefault
  );
  const recipientGroupIds = recipientGroups.map(
    (group: RecipientGroup) => group.id
  );
  for (const filterKey in policyFilter) {
    const filterName = policyFilter[filterKey] as keyof typeof policyFilter;
    if (policyUuid) {
      //edit policy
      if (
        (policy as any)[filterName] &&
        (policy as any)[filterName].notification
      ) {
        const ntfRecipientGroupId = (policy as any)[filterName].notification
          .ntfRecipientGroupId;
        if (ntfRecipientGroupId || ntfRecipientGroupId === 0) {
          if (!recipientGroupIds.includes(ntfRecipientGroupId)) {
            // if group is invalid, set default group id instead
            setPolicy((state: PolicyTemplateProps) => {
              if (typeof (state as any)[filterName] !== 'undefined') {
                (state as any)[filterName].notification.ntfRecipientGroupId =
                  defaultGroup?.id || 1;
              }
            });
          }
        }
      }
    } else {
      // add policy
      // set default group id by default
      setPolicy((state: PolicyTemplateProps) => {
        if (typeof (state as any)[filterName] !== 'undefined') {
          (state as any)[filterName].notification.ntfRecipientGroupId =
            defaultGroup?.id || 1;
        }
      });
    }
  }
};
/*
 * createStore中，包装了原始的set和get方法，分别为setPolicy和getPolicy
 * 使得每个policy filter slice中只关注于policy的结构，否则set state的层级太深了
 */
export const createPolicyStore = (initProps?: {
  template: Partial<PolicyTemplateProps>;
  config: Partial<PolicyConfig>;
  orgId: string;
}) => {
  const { t } = i18nInst;
  let defaultProps: PolicyTemplateProps;
  let defaultConfig: PolicyConfig;
  const initPropsTemplate = initProps?.template;
  const initPropsConfig = initProps?.config;
  if (!initPropsTemplate) {
    defaultProps = getDefaultProps();
  }
  if (!initPropsConfig) {
    defaultConfig = getDefaultConfig();
  }
  return createStore<PolicyStore>()(
    devtools<PolicyStore>(
      (set, get) => {
        const currentOrgId = initProps?.orgId || 'all';
        const setPolicy = (
          payload:
            | Partial<PolicyTemplateProps>
            | ((state: PolicyTemplateProps) => void)
        ) => {
          if (typeof payload === 'function') {
            const newPolicy = produce(payload)(get().template);
            console.log('setPolicy newPolicy', newPolicy);
            // set((state: PolicyStore) => ({
            //   ...state,
            //   template: { ...state.template, ...newPolicy },
            // }));
            set({ template: newPolicy });
          } else {
            set((state: PolicyStore) => ({
              ...state,
              template: { ...state.template, ...payload },
            }));
          }
        };
        const getPolicy = () => get().template;
        return {
          // stash the init policy data
          initTemplate: cloneDeep({ ...defaultProps, ...initPropsTemplate }),
          template: { ...defaultProps, ...initPropsTemplate },
          config: { ...defaultConfig, ...initPropsConfig },
          orgId: currentOrgId,
          ...createGeneralSlice(setPolicy, getPolicy, get),
          ...createAntiSpamSlice(setPolicy, getPolicy, get),
          ...createVirusScanSlice(setPolicy, getPolicy, get),
          ...createFileBlockingSlice(setPolicy, getPolicy, get),
          ...createWebThreatProtectionSlice(setPolicy, getPolicy, get),
          ...createDdaSlice(setPolicy, getPolicy, get),
          ...createDlpFilterSlice(setPolicy, getPolicy, get),
          ...createKeywordExtractionFilterSlice(setPolicy, getPolicy, get),
          ...createShareLinkFilterSlice(setPolicy, getPolicy, get),
          ...createRiskAiFilterSlice(setPolicy, getPolicy, get),

          // tmAntispamfilters: [{ ...createAntiSpamStore(tmAntispamfilters[0])() }],
          activeTabIndex: 0,
          availableTargets: {},
          availableTargetsGerneral: [],
          availableTargetsExtra: [],
          availableTargetDomains: {},
          availableTargetObjects: {},
          availableTargetCommunities: {},
          availableTargetProfiles: {},
          availableTargetObjectsGeneral: [],
          availableTargetCommunitiesGeneral: [],
          availableTargetProfilesGeneral: [],
          recipientGroups: [],
          dlpTemplates: [],
          mipLabels: [],
          tempVars: {
            targetType: TargetType.Domain,
            targetUserGroups: {
              targets: [],
              targetGroups: [],
              targetType: TargetType.UserAndGroup,
              capacity: TargetCapacity.empty,
              empty: true,
            },
            targetDomains: {
              targets: [],
              targetGroups: [],
              targetType: TargetType.Domain,
              empty: true,
            },
            targetObjects: {
              targets: [],
              targetGroups: [],
              targetType: TargetType.Objects,
              empty: true,
            },
            targetCommunities: {
              targets: [],
              targetGroups: [],
              targetType: TargetType.Communities,
              empty: true,
            },
            targetProfiles: {
              targets: [],
              targetGroups: [],
              targetType: TargetType.Profiles,
              capacity: TargetCapacity.empty,
              empty: true,
            },
            searchedTargets: [],
            searchTargetDomains: [],
            searchTargetUserGroups: [],
            searchTargetObjects: [],
            searchTargetCommunities: [],
            searchTargetProfiles: [],
            slfExceptionPaths: [],
            slfExceptionUsers: {
              targets: [],
              targetGroups: [],
              targetType: TargetType.UserAndGroup,
              empty: true,
            },
          },
          setTempVars: (
            payload:
              | Partial<PolicyState['tempVars']>
              | ((s: PolicyState['tempVars']) => void)
          ) => {
            const originTempVars = get().tempVars;
            if (typeof payload === 'function') {
              const newTempVars = produce(payload)(originTempVars);
              set({ tempVars: newTempVars });
            } else {
              set({ tempVars: { ...originTempVars, ...payload } });
            }
          },
          getAllTargetsBase: async (
            initial = false,
            serviceType = Service.Exchange,
            targetType = TargetType.UserAndGroup,
            isSalesforceProfiles = false
          ) => {
            try {
              const currentStore = get();
              const pagination =
                initial === true
                  ? undefined
                  : currentStore.availableTargets.pagination;
              const targetId = '0';
              const resp = await UserApi.getPolicyTargets(
                currentOrgId,
                serviceType,
                targetType,
                targetId,
                pagination
              );
              const { data: availableTargets } = resp;
              let newAvailableTargets = availableTargets;
              if (pagination) {
                // if pagination is not undefined, append new data to origin data, otherwise overwrite the origin data
                const originAvailableTargets = currentStore.availableTargets;
                newAvailableTargets = {
                  ...availableTargets,
                  targetGroups: [
                    ...originAvailableTargets.targetGroups,
                    ...availableTargets.targetGroups,
                  ],
                  targets: [
                    ...originAvailableTargets.targets,
                    ...availableTargets.targets,
                  ],
                };
              }
              // set availableTargets
              set(
                produce((state: PolicyStore) => ({
                  ...state,
                  availableTargets: newAvailableTargets,
                }))
              );
              // set availabelTargetsGerneral
              const usersData = newAvailableTargets.targets;
              const groupsData = newAvailableTargets.targetGroups;
              const userGroups = convertUserGroup(
                usersData,
                groupsData,
                currentStore.template.policyType
              );
              // add show more node
              const isPagination = !!newAvailableTargets.pagination;
              const getMore = () =>
                currentStore.getAllTargetsBase(false, serviceType, targetType);
              const userGroupsWithShowMore = getAllTargetsDisplay(
                userGroups,
                isPagination,
                getMore
              );
              // add All user node
              const { policyType, policySubType } = currentStore.template;
              const policyTypeName = getPolicyName(policyType, policySubType);
              let newAvailableTargetsGerneral;
              if (isSalesforceProfiles) {
                newAvailableTargetsGerneral = [
                  {
                    title: t(
                      `policy.policyTarget.${policyTypeName}.allProfile`
                    ),
                    key: 'all',
                    children: userGroupsWithShowMore,
                    expanded: true,
                  },
                ];
              } else {
                newAvailableTargetsGerneral = [
                  {
                    title: t(`policy.policyTarget.${policyTypeName}.all`),
                    key: 'all',
                    children: userGroupsWithShowMore,
                    expanded: true,
                  },
                ];
              }
              if (isSalesforceProfiles) {
                set({ availableTargetsExtra: newAvailableTargetsGerneral });
              } else {
                set({ availableTargetsGerneral: newAvailableTargetsGerneral });
              }
            } catch (error) {
              window.showNotification({
                titleMsg: t('common.apiException.internalError'),
                severity: 'error',
              });
              console.error(error);
            }
          },
          getAllTargetsByService: async (initial = false) => {
            const currentStore = get();
            const { getAllTargetsBase } = currentStore;
            const { policyType } = currentStore.template;
            const serviceType =
              PolicyType2Service[policyType as unknown as PolicyType];
            if (serviceType) {
              await getAllTargetsBase(initial, serviceType);
            }
          },
          getAllObjectsTargetsByService: async (
            initial = false,
            serviceType = Service.SalesforceSandbox
          ) => {
            try {
              const currentStore = get();
              const pagination =
                initial === true
                  ? undefined
                  : currentStore.availableTargetObjects.pagination;
              const targetType = TargetType.Objects;
              const targetId = '0';
              const resp = await UserApi.getPolicyTargets(
                currentOrgId,
                serviceType,
                targetType,
                targetId,
                pagination,
                currentStore.template.holderType
              );
              const { data: availableTargetObjects } = resp;
              const i18nSalesforceGroupKey = [
                '500',
                'customized',
                '3rd_party',
                'built_in',
                'communities',
                'chatters',
                '00P',
              ];
              if (availableTargetObjects.targetGroups.length !== 0) {
                availableTargetObjects.targetGroups.forEach(
                  (group: TargetGroup) => {
                    if (i18nSalesforceGroupKey.includes(group.title)) {
                      group.title = t(
                        `policy.policyTarget.salesforce.groupTitle.${group.title}`
                      );
                    }
                  }
                );
              }
              let newAvailableTargetObjects = availableTargetObjects;
              if (pagination) {
                // if pagination is not undefined, append new data to origin data, otherwise overwrite the origin data
                const originAvailableTargets =
                  currentStore.availableTargetObjects;
                newAvailableTargetObjects = {
                  ...availableTargetObjects,
                  targetGroups: [
                    ...originAvailableTargets.targetGroups,
                    ...availableTargetObjects.targetGroups,
                  ],
                  targets: [
                    ...originAvailableTargets.targets,
                    ...availableTargetObjects.targets,
                  ],
                };
              }
              // set availableTargetDomains
              set(
                produce((state: PolicyStore) => ({
                  ...state,
                  availableTargetObjects: newAvailableTargetObjects,
                }))
              );

              // set availableTargetsGerneral
              // construct user groups for newChildren
              const usersData = newAvailableTargetObjects.targets;
              const groupsData = newAvailableTargetObjects.targetGroups;
              const userGroups = convertUserGroup(
                usersData,
                groupsData,
                currentStore.template.policyType
              );
              // construct showmore getTargets function
              const isObjectsPagination =
                !!newAvailableTargetObjects.pagination;
              const getMoreTargetObjects = () =>
                currentStore.getAllObjectsTargetsByService(false, serviceType);
              const objectsTargetsWithShowMore = getAllTargetsDisplay(
                userGroups,
                isObjectsPagination,
                getMoreTargetObjects
              );

              const newAvailableTargetsGerneral = [
                {
                  title: t(
                    `policy.policyTarget.salesforceSandbox.dlp.allObjects`
                  ),
                  key: 'all',
                  children: objectsTargetsWithShowMore,
                  expanded: true,
                },
              ];
              set({
                availableTargetObjectsGeneral: newAvailableTargetsGerneral,
              });
            } catch (error) {
              window.showNotification({
                titleMsg: t('common.apiException.internalError'),
                severity: 'error',
              });
              console.error(error);
            }
          },
          getAllCommunitiesTargetsByService: async (
            initial = false,
            serviceType = Service.SalesforceSandbox
          ) => {
            try {
              const currentStore = get();
              const pagination =
                initial === true
                  ? undefined
                  : currentStore.availableTargetCommunities.pagination;
              const targetType = TargetType.Communities;
              const targetId = '0';
              const resp = await UserApi.getPolicyTargets(
                currentOrgId,
                serviceType,
                targetType,
                targetId,
                pagination
              );
              const { data: availableTargetCommunities } = resp;
              let newAvailableTargetCommunities = availableTargetCommunities;
              if (pagination) {
                // if pagination is not undefined, append new data to origin data, otherwise overwrite the origin data
                const originAvailableTargets =
                  currentStore.availableTargetCommunities;
                newAvailableTargetCommunities = {
                  ...availableTargetCommunities,
                  targetGroups: [
                    ...originAvailableTargets.targetGroups,
                    ...availableTargetCommunities.targetGroups,
                  ],
                  targets: [
                    ...originAvailableTargets.targets,
                    ...availableTargetCommunities.targets,
                  ],
                };
              }
              // set availableTargetDomains
              set(
                produce((state: PolicyStore) => ({
                  ...state,
                  availableTargetCommunities: newAvailableTargetCommunities,
                }))
              );

              const usersData = newAvailableTargetCommunities.targets;
              const groupsData = newAvailableTargetCommunities.targetGroups;
              const userGroups = convertUserGroup(
                usersData,
                groupsData,
                currentStore.template.policyType
              );
              // construct showmore getTargets function
              const isObjectsPagination =
                !!newAvailableTargetCommunities.pagination;
              const getMoreTargetObjects = () =>
                currentStore.getAllCommunitiesTargetsByService(
                  false,
                  serviceType
                );
              const objectsTargetsWithShowMore = getAllTargetsDisplay(
                userGroups,
                isObjectsPagination,
                getMoreTargetObjects
              );

              const newAvailableTargetsGerneral = [
                {
                  title: t(
                    `policy.policyTarget.salesforceSandbox.dlp.allObjects`
                  ),
                  key: 'all',
                  children: objectsTargetsWithShowMore,
                  expanded: true,
                },
              ];
              set({
                availableTargetCommunitiesGeneral: newAvailableTargetsGerneral,
              });
            } catch (error) {
              window.showNotification({
                titleMsg: t('common.apiException.internalError'),
                severity: 'error',
              });
              console.error(error);
            }
          },
          getAllProfilesTargetsByService: async (
            initial = false,
            serviceType = Service.SalesforceSandbox
          ) => {
            try {
              const currentStore = get();
              const pagination =
                initial === true
                  ? undefined
                  : currentStore.availableTargetProfiles.pagination;
              const targetType = TargetType.Profiles;
              const targetId = '0';
              const resp = await UserApi.getPolicyTargets(
                currentOrgId,
                serviceType,
                targetType,
                targetId,
                pagination
              );
              const { data: availableTargetProfiles } = resp;
              let newAvailableTargetProfiles = availableTargetProfiles;
              if (pagination) {
                // if pagination is not undefined, append new data to origin data, otherwise overwrite the origin data
                const originAvailableTargets =
                  currentStore.availableTargetProfiles;
                newAvailableTargetProfiles = {
                  ...availableTargetProfiles,
                  targetGroups: [
                    ...originAvailableTargets.targetGroups,
                    ...availableTargetProfiles.targetGroups,
                  ],
                  targets: [
                    ...originAvailableTargets.targets,
                    ...availableTargetProfiles.targets,
                  ],
                };
              }
              // set availableTargetDomains
              set(
                produce((state: PolicyStore) => ({
                  ...state,
                  availableTargetProfiles: newAvailableTargetProfiles,
                }))
              );

              const usersData = newAvailableTargetProfiles.targets;
              const groupsData = newAvailableTargetProfiles.targetGroups;
              const userGroups = convertUserGroup(
                usersData,
                groupsData,
                currentStore.template.policyType
              );
              // construct showmore getTargets function
              const isObjectsPagination =
                !!newAvailableTargetProfiles.pagination;
              const getMoreTargetObjects = () =>
                currentStore.getAllProfilesTargetsByService(false, serviceType);
              const objectsTargetsWithShowMore = getAllTargetsDisplay(
                userGroups,
                isObjectsPagination,
                getMoreTargetObjects
              );

              const newAvailableTargetsGerneral = [
                {
                  title: t(
                    `policy.policyTarget.salesforceSandbox.dlp.allObjects`
                  ),
                  key: 'all',
                  children: objectsTargetsWithShowMore,
                  expanded: true,
                },
              ];
              set({
                availableTargetProfilesGeneral: newAvailableTargetsGerneral,
              });
            } catch (error) {
              window.showNotification({
                titleMsg: t('common.apiException.internalError'),
                severity: 'error',
              });
              console.error(error);
            }
          },
          getAllHpuTargetsByService: async (initial = false) => {
            const currentStore = get();
            const { getAllTargetsBase } = currentStore;
            const { policyType } = currentStore.template;
            const serviceType =
              PolicyType2Service[policyType as unknown as PolicyType];
            if (serviceType) {
              await getAllTargetsBase(initial, serviceType, TargetType.HPU);
            }
          },
          getAllLocalAccountsTargets: async (initial = false) => {
            try {
              const currentStore = get();
              const pagination =
                initial === true
                  ? undefined
                  : currentStore.availableTargets.users.pagination;
              const targetId = '0';
              const resp = await UserApi.getLocalAccountTargets(
                currentOrgId,
                targetId,
                pagination
              );
              const { data: availableTargets } = resp;
              let newAvailableTargets = availableTargets;
              if (pagination) {
                // if pagination is not undefined, append new data to origin data, otherwise overwrite the origin data
                const originAvailableTargets = currentStore.availableTargets;
                newAvailableTargets = {
                  accounts: { ...originAvailableTargets.accounts },
                  users: {
                    ...availableTargets.users,
                    targetGroups: [
                      ...originAvailableTargets.users.targetGroups,
                      ...availableTargets.users.targetGroups,
                    ],
                    targets: [
                      ...originAvailableTargets.users.targets,
                      ...availableTargets.users.targets,
                    ],
                  },
                };
              }
              // set availableTargets
              set(
                produce((state: PolicyStore) => ({
                  ...state,
                  availableTargets: newAvailableTargets,
                }))
              );
              // set availabelTargetsGerneral
              const usersData = newAvailableTargets.users.targets;
              const groupsData = newAvailableTargets.users.targetGroups;
              const localUsersData = newAvailableTargets.accounts.targets;
              const userGroups = convertUserGroup(
                usersData,
                groupsData,
                currentStore.template.policyType
              );
              // userGroups.unshift({
              //   title: 'Added Users',
              //   key: 'local user',
              //   icon: 'user-team',
              //   children: convertUserGroup(localUsersData, []),
              // });
              // add show more node
              const isPagination = !!newAvailableTargets.users.pagination;
              const getMore = () =>
                currentStore.getAllLocalAccountsTargets(false);
              const userGroupsWithShowMore = getAllTargetsDisplay(
                userGroups,
                isPagination,
                getMore
              );
              const localUserGroups = getAllTargetsDisplay(
                convertUserGroup(
                  localUsersData,
                  [],
                  currentStore.template.policyType
                ),
                false,
                () => {}
              );
              const newAvailableTargetsGerneral = [
                {
                  title: t(`policy.policyTarget.exchange.all`),
                  key: 'all',
                  children: [
                    {
                      title: t('common.label.allADUsers'),
                      key: 'allADUser',
                      children: userGroupsWithShowMore,
                      expanded: false,
                      disableCheckbox: true,
                    },
                    {
                      title: t('common.label.allLocalUsers'),
                      key: 'allLocalUser',
                      children: localUserGroups,
                      expanded: false,
                      disableCheckbox: true,
                    },
                  ],
                  expanded: true,
                },
              ];
              set({ availableTargetsGerneral: newAvailableTargetsGerneral });
            } catch (error) {
              window.showNotification({
                titleMsg: t('common.apiException.internalError'),
                severity: 'error',
              });
              console.error(error);
            }
          },
          getSpecificTargetBasic: async ({
            targetIds,
            serviceType = Service.Exchange,
            targetType = TargetType.UserAndGroup,
            pagination,
            category,
          }: {
            targetIds: string[];
            serviceType: Service;
            targetType: TargetType;
            pagination?: Targets['pagination'];
            category?: PolicyHolderTypeEnum;
          }) => {
            try {
              if (
                !targetIds ||
                targetIds.length === 0 ||
                (targetIds.length === 1 && targetIds[0] === 'all')
              ) {
                return;
              }
              const currentStore = get();
              const { policyType } = currentStore.template;
              const originalTargetIds = cloneDeep(targetIds);
              const targetId = targetIds.pop();
              const {
                availableTargets,
                availableTargetCommunities,
                availableTargetObjects,
                availableTargetProfiles,
                availableTargetObjectsGeneral,
                tempVars: { searchTargetUserGroups },
                setTempVars,
              } = get();
              const isSearchUser = searchTargetUserGroups.length > 0;
              const resp = category
                ? await UserApi.getPolicyTargets(
                    currentOrgId,
                    serviceType,
                    targetType,
                    targetId as string,
                    pagination,
                    category
                  )
                : await UserApi.getPolicyTargets(
                    currentOrgId,
                    serviceType,
                    targetType,
                    targetId as string,
                    pagination
                  );
              const { data: newTargets } = resp;
              if (newTargets.capacity === TargetCapacity.empty) {
                return;
              }
              // remove 'all' / 'search' from the targetIds
              if (targetIds[0] === 'all' || targetIds[0] === 'search') {
                targetIds.shift();
              }
              if (!isSearchUser) {
                // all user
                let newAllTargets: any;
                if (TargetType.Objects === targetType) {
                  newAllTargets = cloneDeep(availableTargetObjects);
                } else if (TargetType.Communities === targetType) {
                  newAllTargets = cloneDeep(availableTargetCommunities);
                } else if (TargetType.Profiles === targetType) {
                  newAllTargets = cloneDeep(availableTargetProfiles);
                } else {
                  newAllTargets = cloneDeep(availableTargets);
                }
                // insert the newTargets into the newAllTargets
                let targetGroups = newAllTargets.targetGroups || [];
                if (targetIds.length > 0) {
                  targetIds.forEach((id: string) => {
                    targetGroups = targetGroups.filter(
                      (group: TargetGroup) => group.key === id
                    )[0]?.children?.targetGroups;
                  });
                }
                let newChildren: Targets = newTargets;
                targetGroups.forEach((group: TargetGroup) => {
                  if (group.key === targetId) {
                    // if pagination is not undefined, append new data to origin data, otherwise overwrite the origin data
                    if (pagination) {
                      if (group.children) {
                        const originTargets = group.children;
                        newChildren = {
                          ...newTargets,
                          targetGroups: [
                            ...originTargets.targetGroups,
                            ...newTargets.targetGroups,
                          ],
                          targets: [
                            ...originTargets.targets,
                            ...newTargets.targets,
                          ],
                        };
                        group.children = newChildren;
                      }
                    } else {
                      newChildren = newTargets;
                      group.children = newTargets;
                    }
                  }
                });
                // set availableTargets
                type SalesforceTargetType =
                  | TargetType.Objects
                  | TargetType.Communities
                  | TargetType.Profiles;

                const targetMap = {
                  [TargetType.Objects]: 'availableTargetObjects',
                  [TargetType.Communities]: 'availableTargetCommunities',
                  [TargetType.Profiles]: 'availableTargetProfiles',
                };

                const targetKey =
                  targetMap[targetType as SalesforceTargetType] ||
                  'availableTargets';

                set(
                  produce((state: PolicyStore) => ({
                    ...state,
                    [targetKey]: newAllTargets,
                  }))
                );

                // set availableTargetsGerneral
                // construct user groups for newChildren
                const usersData = newChildren.targets;
                const groupsData = newChildren.targetGroups;
                const userGroups = convertUserGroup(
                  usersData,
                  groupsData,
                  currentStore.template.policyType
                );
                // construct showmore getTargets function
                const showMoreGetTargets = () => {
                  currentStore.getSpecificTargetBasic({
                    targetIds: originalTargetIds,
                    serviceType,
                    targetType,
                    pagination: newTargets.pagination,
                  });
                };
                // add show more node
                const isPagination = !!newTargets.pagination;

                const userGroupsWithShowMore = getAllTargetsDisplay(
                  userGroups,
                  isPagination,
                  showMoreGetTargets
                );

                // add into availableTargetsGerneral
                let newAvailableTargetsGerneral: any;
                if (TargetType.Objects === targetType) {
                  newAvailableTargetsGerneral = cloneDeep(
                    currentStore.availableTargetObjectsGeneral
                  );
                } else if (TargetType.Communities === targetType) {
                  newAvailableTargetsGerneral = cloneDeep(
                    currentStore.availableTargetCommunitiesGeneral
                  );
                } else if (TargetType.Profiles === targetType) {
                  newAvailableTargetsGerneral = cloneDeep(
                    currentStore.availableTargetProfilesGeneral
                  );
                } else {
                  newAvailableTargetsGerneral = cloneDeep(
                    currentStore.availableTargetsGerneral
                  );
                }
                // the first node is all, all's children are users and groups
                let targetNodes: any =
                  newAvailableTargetsGerneral[0]?.children || [];
                // targetIds has removed all and the last targetId
                if (targetIds.length > 0) {
                  targetIds.forEach((id: string) => {
                    targetNodes = targetNodes.filter(
                      (group: TreeNode) => group.key === id
                    )[0]?.children;
                  });
                }
                targetNodes.forEach((group: TreeNode) => {
                  if (group.key === targetId) {
                    group.children = userGroupsWithShowMore;
                  }
                });

                const targetGeneralMap = {
                  [TargetType.Objects]: 'availableTargetObjectsGeneral',
                  [TargetType.Communities]: 'availableTargetCommunitiesGeneral',
                  [TargetType.Profiles]: 'availableTargetProfilesGeneral',
                };

                const targetGeneralKey =
                  targetGeneralMap[targetType as SalesforceTargetType] ||
                  'availableTargetsGerneral';

                set({
                  [targetGeneralKey]: newAvailableTargetsGerneral,
                });
              } else {
                // search user
                let newSearchTargets = cloneDeep(searchTargetUserGroups);
                // the first node is search, search's children are users and groups
                let targetNodes: any = newSearchTargets[0].children;
                // targetIds has removed all and the last targetId
                if (targetIds.length > 0) {
                  targetIds.forEach((id: string) => {
                    targetNodes = targetNodes.filter(
                      (group: TreeNode) => group.key === id
                    )[0]?.children;
                  });
                }
                let newChildren = newTargets;
                targetNodes.forEach((group: TreeNode) => {
                  if (group.key === targetId) {
                    // group is not empty
                    if (
                      Array.isArray(group.children) &&
                      group.children.length > 0
                    ) {
                      const originalTargets = convertToTargets(group.children);
                      if (originalTargets) {
                        newChildren = {
                          ...newTargets,
                          targetGroups: [
                            ...originalTargets.targetGroups,
                            ...newTargets.targetGroups,
                          ],
                          targets: [
                            ...originalTargets.targets,
                            ...newTargets.targets,
                          ],
                        };
                      }
                    }
                  }
                });

                // construct user groups for newChildren
                const usersData = newChildren.targets;
                const groupsData = newChildren.targetGroups;
                const userGroups = convertUserGroup(
                  usersData,
                  groupsData,
                  currentStore.template.policyType
                );
                // construct showmore getTargets function
                const showMoreGetTargets = () => {
                  currentStore.getSpecificTargetBasic({
                    targetIds: originalTargetIds,
                    serviceType,
                    targetType,
                    pagination: newTargets.pagination,
                  });
                };
                // add show more node
                const isPagination = !!newTargets.pagination;

                const userGroupsWithShowMore = getAllTargetsDisplay(
                  userGroups,
                  isPagination,
                  showMoreGetTargets
                );
                targetNodes.forEach((group: TreeNode) => {
                  if (group.key === targetId) {
                    group.children = userGroupsWithShowMore;
                  }
                });
                // post handler for teams
                if (policyType === PolicyTypeEnum.Teams) {
                  newSearchTargets = handleAllTargetsForTeams(newSearchTargets);
                }
                setTempVars({ searchTargetUserGroups: newSearchTargets });
              }
            } catch (error) {
              window.showNotification({
                titleMsg: t('common.apiException.internalError'),
                severity: 'error',
              });
              console.error(error);
            }
          },
          getSpecificTargetByService: async (
            targetIds: string[],
            pagination?: Targets['pagination']
          ) => {
            const currentStore = get();
            const { getSpecificTargetBasic } = currentStore;
            const { policyType } = currentStore.template;
            const serviceType =
              PolicyType2Service[policyType as unknown as PolicyType];
            const targetType = TargetType.UserAndGroup;
            if (serviceType) {
              await getSpecificTargetBasic({
                targetIds,
                serviceType,
                targetType,
                pagination,
              });
            }
          },
          getSpecificHpuTargetByService: async (
            targetIds: string[],
            pagination?: Targets['pagination']
          ) => {
            const currentStore = get();
            const { getSpecificTargetBasic } = currentStore;
            const { policyType } = currentStore.template;
            const serviceType =
              PolicyType2Service[policyType as unknown as PolicyType];
            const targetType = TargetType.HPU;
            if (serviceType) {
              await getSpecificTargetBasic({
                targetIds,
                serviceType,
                targetType,
                pagination,
              });
            }
          },
          getSpecificLocalAccountsTargets: async (
            targetIds: string[],
            pagination?: Targets['pagination']
          ) => {
            try {
              if (
                !targetIds ||
                targetIds.length === 0 ||
                (targetIds.length === 1 && targetIds[0] === 'all')
              ) {
                return;
              }
              const currentStore = get();
              const originalTargetIds = cloneDeep(targetIds);
              const targetId = targetIds.pop();
              const {
                availableTargets,
                tempVars: { searchTargetUserGroups },
                setTempVars,
              } = get();
              const isSearchUser = searchTargetUserGroups.length > 0;
              const resp = await UserApi.getLocalAccountTargets(
                currentOrgId,
                targetId as string,
                pagination
              );
              const { data: newTargets } = resp;
              if (newTargets.capacity === TargetCapacity.empty) {
                return;
              }
              targetIds = targetIds.filter(
                (id) =>
                  !['all', 'search', 'allADUser', 'allLocalUser'].includes(id)
              );
              if (!isSearchUser) {
                // all user
                const newAllTargets = cloneDeep(availableTargets.users);
                // insert the newTargets into the newAllTargets
                let targetGroups = newAllTargets.targetGroups;
                if (targetIds.length > 0) {
                  targetIds.forEach((id: string) => {
                    targetGroups = targetGroups.filter(
                      (group: TargetGroup) => group.key === id
                    )[0]?.children?.targetGroups;
                  });
                }
                let newChildren: Targets = newTargets;
                targetGroups.forEach((group: TargetGroup) => {
                  if (group.key === targetId) {
                    // if pagination is not undefined, append new data to origin data, otherwise overwrite the origin data
                    if (pagination) {
                      if (group.children) {
                        const originTargets = group.children;
                        newChildren = {
                          ...newTargets,
                          targetGroups: [
                            ...originTargets.targetGroups,
                            ...newTargets.targetGroups,
                          ],
                          targets: [
                            ...originTargets.targets,
                            ...newTargets.targets,
                          ],
                        };
                        group.children = newChildren;
                      }
                    } else {
                      newChildren = newTargets;
                      group.children = newTargets;
                    }
                  }
                });
                // set availableTargets
                set(
                  produce((state: PolicyStore) => ({
                    ...state,
                    availableTargets: {
                      ...state.availableTargets,
                      users: newAllTargets,
                    },
                  }))
                );
                // set availableTargetsGerneral
                // construct user groups for newChildren
                const usersData = newChildren.targets;
                const groupsData = newChildren.targetGroups;
                const userGroups = convertUserGroup(
                  usersData,
                  groupsData,
                  currentStore.template.policyType
                );

                // construct showmore getTargets function
                const showMoreGetTargets = () => {
                  currentStore.getSpecificLocalAccountsTargets(
                    originalTargetIds,
                    newTargets.pagination
                  );
                };
                // add show more node
                const isPagination = !!newTargets.pagination;

                const userGroupsWithShowMore = getAllTargetsDisplay(
                  userGroups,
                  isPagination,
                  showMoreGetTargets
                );
                // add into availableTargetsGerneral
                const newAvailableTargetsGerneral = cloneDeep(
                  currentStore.availableTargetsGerneral
                );
                // the first node is all, all's children are users and groups
                let targetNodes: any;
                if (
                  newAvailableTargetsGerneral[0] &&
                  newAvailableTargetsGerneral[0].children &&
                  newAvailableTargetsGerneral[0].children[0] &&
                  newAvailableTargetsGerneral[0].children[0].children
                ) {
                  targetNodes =
                    newAvailableTargetsGerneral[0].children[0].children;
                }
                // targetIds has removed all and the last targetId
                if (targetIds) {
                  targetIds.forEach((id: string) => {
                    targetNodes = targetNodes.filter(
                      (group: TreeNode) => group.key === id
                    )[0]?.children;
                  });
                }
                targetNodes.forEach((group: TreeNode) => {
                  if (group.key === targetId) {
                    group.children = userGroupsWithShowMore;
                  }
                });
                set({ availableTargetsGerneral: newAvailableTargetsGerneral });
              } else {
                // search user
                const newSearchTargets = cloneDeep(searchTargetUserGroups);
                // the first node is search, search's children are users and groups
                let targetNodes: any = newSearchTargets[0].children;
                // targetIds has removed all and the last targetId
                if (targetIds.length > 0) {
                  targetIds.forEach((id: string) => {
                    targetNodes = targetNodes.filter(
                      (group: TreeNode) => group.key === id
                    )[0]?.children;
                  });
                }
                let newChildren = newTargets;
                targetNodes.forEach((group: TreeNode) => {
                  if (group.key === targetId) {
                    // group is not empty
                    if (
                      Array.isArray(group.children) &&
                      group.children.length > 0
                    ) {
                      const originalTargets = convertToTargets(group.children);
                      if (originalTargets) {
                        newChildren = {
                          ...newTargets,
                          targetGroups: [
                            ...originalTargets.targetGroups,
                            ...newTargets.targetGroups,
                          ],
                          targets: [
                            ...originalTargets.targets,
                            ...newTargets.targets,
                          ],
                        };
                      }
                    }
                  }
                });

                // construct user groups for newChildren
                const usersData = newChildren.targets;
                const groupsData = newChildren.targetGroups;
                const userGroups = convertUserGroup(
                  usersData,
                  groupsData,
                  currentStore.template.policyType
                );
                // construct showmore getTargets function
                const showMoreGetTargets = () => {
                  currentStore.getSpecificLocalAccountsTargets(
                    originalTargetIds,
                    newTargets.pagination
                  );
                };
                // add show more node
                const isPagination = !!newTargets.pagination;

                const userGroupsWithShowMore = getAllTargetsDisplay(
                  userGroups,
                  isPagination,
                  showMoreGetTargets
                );
                targetNodes.forEach((group: TreeNode) => {
                  if (group.key === targetId) {
                    group.children = userGroupsWithShowMore;
                  }
                });
                setTempVars({
                  searchTargetUserGroups: unCheckTargetGroup(newSearchTargets),
                });
              }
            } catch (error) {
              window.showNotification({
                titleMsg: t('common.apiException.internalError'),
                severity: 'error',
              });
              console.error(error);
            }
          },
          getSpecificObjectTargetByService: async (
            targetIds: string[],
            pagination?: Targets['pagination']
          ) => {
            const currentStore = get();
            const { getSpecificTargetBasic } = currentStore;
            const { policyType, holderType } = currentStore.template;
            const serviceType =
              PolicyType2Service[policyType as unknown as PolicyType];
            const targetType = TargetType.Objects;
            if (serviceType) {
              await getSpecificTargetBasic({
                targetIds,
                serviceType,
                targetType,
                pagination,
                category: holderType,
              });
            }
          },
          getSpecificCommunityTargetByService: async (
            targetIds: string[],
            pagination?: Targets['pagination']
          ) => {
            const currentStore = get();
            const { getSpecificTargetBasic } = currentStore;
            const { policyType } = currentStore.template;
            const serviceType =
              PolicyType2Service[policyType as unknown as PolicyType];
            const targetType = TargetType.Communities;
            if (serviceType) {
              await getSpecificTargetBasic({
                targetIds,
                serviceType,
                targetType,
                pagination,
              });
            }
          },
          getSpecificProfileTargetByService: async (
            targetIds: string[],
            pagination?: Targets['pagination']
          ) => {
            const currentStore = get();
            const { getSpecificTargetBasic } = currentStore;
            const { policyType } = currentStore.template;
            const serviceType =
              PolicyType2Service[policyType as unknown as PolicyType];
            const targetType = TargetType.Profiles;
            if (serviceType) {
              await getSpecificTargetBasic({
                targetIds,
                serviceType,
                targetType,
                pagination,
              });
            }
          },
          getRecipientGroups: async () => {
            const policy = get().template;
            const { policyUuid } = policy;
            try {
              const resp = await GlobalApi.getRecipientGroupsForPolicy();
              const { data: recipientGroups } = resp;
              const holderType = policy.holderType || policy.policyHolderType;
              if (holderType === PolicyHolderTypeEnum.ATP) {
                setRecipientGroupInPolicy(
                  policy,
                  PolicyFilters,
                  recipientGroups,
                  setPolicy
                );
              } else if (holderType === PolicyHolderTypeEnum.DLP) {
                setRecipientGroupInPolicy(
                  policy,
                  DlpPolicyFilters,
                  recipientGroups,
                  setPolicy
                );
              }
              // set recipient groups
              set(
                produce((state: PolicyStore) => ({
                  ...state,
                  recipientGroups,
                }))
              );
            } catch (error) {
              window.showNotification({
                titleMsg: t('common.apiException.internalError'),
                severity: 'error',
              });
              console.error(error);
            }
          },
          getValidateArr: () => {
            const {
              validateGeneral,
              validateAntispam,
              validateVirusScan,
              validateFileBlocking,
              validateWebReputation,
              validateVirtualAnalyzer,
              validateGeneralInline,
              validateGeneralSalesforce,
              validateDlp,
              validateDlpSalesforce,
              validateKeywordExtraction,
              validateShareLink,
              validateRiskAi,
              template: { policyType, holderType, policySubType },
            } = get();
            let validateArr: (() => any)[] = [];
            const validateArrAtpEmail = [
              validateGeneral,
              validateAntispam,
              validateVirusScan,
              validateFileBlocking,
              validateWebReputation,
              validateVirtualAnalyzer,
            ];
            const validateArrAtpInline = [
              validateGeneralInline,
              validateAntispam,
              validateVirusScan,
              validateFileBlocking,
              validateWebReputation,
              validateVirtualAnalyzer,
            ];
            const validateArrAtpCollaboration = [
              validateGeneral,
              validateVirusScan,
              validateFileBlocking,
              validateWebReputation,
              validateVirtualAnalyzer,
            ];
            const validateArrDlpBox = [
              validateGeneral,
              validateDlp,
              validateKeywordExtraction,
            ];
            const validateArrDlpBoxShareLink = [
              validateGeneral,
              validateShareLink,
            ];
            const validateArrAtpSalesforce = [
              validateGeneralSalesforce,
              validateVirusScan,
              validateFileBlocking,
              validateWebReputation,
              validateVirtualAnalyzer,
            ];
            const validateArrDlpDefault = [validateGeneral, validateDlp];
            const validateArrDlpInline = [validateGeneralInline, validateDlp];
            const validateArrDlpSalesforce = [
              validateDlpSalesforce,
              validateDlp,
            ];
            if (holderType === PolicyHolderTypeEnum.ATP) {
              switch (policyType) {
                case PolicyTypeEnum.Exchange:
                  validateArr = [...validateArrAtpEmail, validateRiskAi];
                  break;
                case PolicyTypeEnum.Gmail:
                  validateArr = [...validateArrAtpEmail, validateRiskAi];
                  break;
                case PolicyTypeEnum.ExchangeInline:
                  if (policySubType === PolicySubTypeEnum.inbound) {
                    validateArr = [...validateArrAtpInline, validateRiskAi];
                  } else {
                    validateArr = validateArrAtpInline;
                  }
                  break;
                case PolicyTypeEnum.GmailInline:
                  if (policySubType === PolicySubTypeEnum.inbound) {
                    validateArr = [...validateArrAtpInline, validateRiskAi];
                  } else {
                    validateArr = validateArrAtpInline;
                  }
                  break;
                case PolicyTypeEnum.Teams:
                case PolicyTypeEnum.TeamsChat:
                case PolicyTypeEnum.OneDrive:
                case PolicyTypeEnum.SharePoint:
                case PolicyTypeEnum.Box:
                case PolicyTypeEnum.Dropbox:
                case PolicyTypeEnum.GoogleDrive:
                  validateArr = validateArrAtpCollaboration;
                  break;
                case PolicyTypeEnum.SalesforceSandbox:
                case PolicyTypeEnum.SalesforceProduction:
                  validateArr = validateArrAtpSalesforce;
                  break;
                default:
                  break;
              }
            } else if (holderType === PolicyHolderTypeEnum.DLP) {
              if (policyType === PolicyTypeEnum.Box) {
                if (policySubType === 1) {
                  validateArr = validateArrDlpBoxShareLink;
                } else {
                  validateArr = validateArrDlpBox;
                }
              } else if (
                policyType === PolicyTypeEnum.ExchangeInline ||
                policyType === PolicyTypeEnum.GmailInline
              ) {
                validateArr = validateArrDlpInline;
              } else if (
                policyType === PolicyTypeEnum.SalesforceProduction ||
                policyType === PolicyTypeEnum.SalesforceSandbox
              ) {
                validateArr = validateArrDlpSalesforce;
              } else {
                validateArr = validateArrDlpDefault;
              }
            }
            return validateArr;
          },
          validateAll: () => {
            /*
             * errors: {
             *   filterName: {
             *     key: {
             *        title: 'error title',
             *        content: 'error content',
             *     }
             *   }
             * }
             *
             */
            const currentStore = get();
            const validates = currentStore.getValidateArr();
            console.log('validates', validates);
            for (const method of validates) {
              const errors = method();
              if (errors) {
                handleValidateError({ errors, isValidateAll: true });
                return false;
              }
            }

            const policy = get().template;
            if (policy.policyRealtimeEnabled) {
              // Make sure at least one filter is enabled for ATP and DLP policies
              const hasEnabledFilter = checkIfPolicyHasEnabledFilter(policy);
              if (!hasEnabledFilter) {
                window.showNotification({
                  titleMsg: t('errors.policy.atLeastOneFilterEnabled'),
                  severity: 'error',
                });
                return false;
              }
            }

            return true;
          },
          submitPolicy: async ({
            successCallback,
            policyId,
          }: {
            successCallback: () => void;
            policyId?: string;
          }) => {
            const currentStore = get();
            const { validateAll } = currentStore;
            if (validateAll()) {
              // submit template data
              const policy = cloneDeep(getPolicy());
              policy.policyLang = localStorage.getItem('i18nextLng') || 'en';
              // backend pass holderType as policyHolderType
              policy.policyHolderType = policy.holderType;
              const policyStr = JSON.stringify(policy);

              const callback = async () => {
                try {
                  if (!policyId) {
                    await PolicyApi.createPolicy(policyStr);
                    reportPendo('Policy.Action.Add', {
                      policyName: policy.policyName,
                      type: policy.policyType,
                      subType: policy.policySubType,
                    });
                  } else {
                    await PolicyApi.updatePolicy(policyStr, policyId);
                    reportPendo('Policy.Action.Update', {
                      uuid: policy.policyUuid,
                      type: policy.policyType,
                      subType: policy.policySubType,
                      policyName: policy.policyName,
                    });
                  }
                  // Invalidate anomaly configuration query since policy changes might affect it
                  queryClient.invalidateQueries({
                    queryKey: ['anomalyConfigured', currentOrgId],
                  });
                } catch (error: any) {
                  if (
                    error?.data?.error?.details?.[0]?.code ===
                    'POLICY_API;MAX_COUNT_LIMIT'
                  ) {
                    const { target, limit, policyHolder } =
                      error.data.error.details[0];
                    const serviceName = t(`common.service.${target}`);
                    const policyHolderString = t(
                      `dashboard.sideBar.${policyHolder}`
                    );
                    window.showNotification({
                      titleMsg: t('errors.policy.service.outOfLimitation', {
                        POLICY_HOLDER: policyHolderString,
                        SERVICE: serviceName,
                        NUMBER: limit,
                      }),
                      severity: 'error',
                    });
                  } else if (
                    error?.data?.error?.details?.[0]?.code ===
                    'POLICY_API;NO_FILTER_ENABLED'
                  ) {
                    console.error(error);
                    window.showNotification({
                      titleMsg: t('errors.policy.atLeastOneFilterEnabled'),
                      severity: 'error',
                    });
                  } else if (error?.data?.error?.details?.[0]?.code) {
                    console.error(error);
                    const errorCode = error?.data?.error?.details?.[0]?.code;
                    window.showNotification({
                      titleMsg: t('errors.policy.service.' + errorCode),
                      severity: 'error',
                    });
                  } else {
                    console.error(error);
                    window.showNotification({
                      titleMsg: t('common.apiException.internalError'),
                      severity: 'error',
                    });
                  }
                }

                successCallback();
              };

              const isInlinePolicy =
                policy.policyType === PolicyTypeEnum.GmailInline ||
                policy.policyType === PolicyTypeEnum.ExchangeInline;

              // create policy
              if (
                isInlinePolicy &&
                policy.policyTarget.targetType === 'user' &&
                policy.policyTarget.capacity === 'all'
              ) {
                window.dispatchEvent(
                  new CustomEvent(GmailInlinePolicyEvent.SelectAllUserTarget, {
                    detail: {
                      callback,
                    },
                  })
                );
                return;
              }

              // update inline policy target type
              if (
                isInlinePolicy &&
                policyId &&
                policy.policyTarget.targetType !==
                  get().initTemplate.policyTarget.targetType
              ) {
                window.dispatchEvent(
                  new CustomEvent(GmailInlinePolicyEvent.SwitchTargetType, {
                    detail: {
                      type: policy.policySubType,
                      callback,
                    },
                  })
                );
                return;
              }

              callback();
            }
          },
          setActiveTabIndex: (index: number) => {
            const { getValidateArr, activeTabIndex } = get();
            const validateArr: (() => any)[] = getValidateArr();

            // Safety check: ensure validateArr has a function at activeTabIndex
            if (
              validateArr[activeTabIndex] &&
              typeof validateArr[activeTabIndex] === 'function'
            ) {
              const errors = validateArr[activeTabIndex]();
              if (errors) {
                handleValidateError({ errors });
                return;
              }
            }

            set((state: PolicyStore) => ({
              ...state,
              activeTabIndex: index,
            }));
          },
          getAllTargetDomainsBase: async (
            initial = false,
            serviceType = Service.ExchangeInline
          ) => {
            try {
              const currentStore = get();
              const pagination =
                initial === true
                  ? undefined
                  : currentStore.availableTargetDomains.pagination;
              const targetType = TargetType.Domain;
              const targetId = '0';
              const resp = await UserApi.getPolicyTargets(
                currentOrgId,
                serviceType,
                targetType,
                targetId,
                pagination
              );
              const { data: availableTargetDomains } = resp;
              let newAvailableTargetDomains = availableTargetDomains;
              if (pagination) {
                // if pagination is not undefined, append new data to origin data, otherwise overwrite the origin data
                const originAvailableTargets =
                  currentStore.availableTargetDomains;
                newAvailableTargetDomains = {
                  ...availableTargetDomains,
                  targetGroups: [
                    ...originAvailableTargets.targetGroups,
                    ...availableTargetDomains.targetGroups,
                  ],
                  targets: [
                    ...originAvailableTargets.targets,
                    ...availableTargetDomains.targets,
                  ],
                };
              }
              // set availableTargetDomains
              set(
                produce((state: PolicyStore) => ({
                  ...state,
                  availableTargetDomains: newAvailableTargetDomains,
                }))
              );
            } catch (error) {
              window.showNotification({
                titleMsg: t('common.apiException.internalError'),
                severity: 'error',
              });
              console.error(error);
            }
          },
          getAllTargetDomainsByService: async (initial = false) => {
            const currentStore = get();
            const { getAllTargetDomainsBase } = currentStore;
            const { policyType } = currentStore.template;
            const serviceType =
              PolicyType2Service[policyType as unknown as PolicyType];
            if (serviceType) {
              await getAllTargetDomainsBase(initial, serviceType);
            }
          },
          syncUsersByService: async () => {
            try {
              const currentStore = get();
              const { policyType } = currentStore.template;
              const serviceType =
                PolicyType2Service[policyType as unknown as PolicyType];
              if (serviceType) {
                await PolicyApi.syncTargetUsersByServiceOnDemand(serviceType);
              }
            } catch (error) {
              window.showNotification({
                titleMsg: t('common.apiException.internalError'),
                severity: 'error',
                error,
              });
              console.error(error);
            }
          },
          syncDomainsByService: async () => {
            try {
              const currentStore = get();
              const { policyType } = currentStore.template;
              const serviceType =
                PolicyType2Service[policyType as unknown as PolicyType];
              if (serviceType) {
                await PolicyApi.syncTargetDomainsByServiceOnDemand(serviceType);
              }
            } catch (error) {
              window.showNotification({
                titleMsg: t('common.apiException.internalError'),
                severity: 'error',
              });
              console.error(error);
            }
          },
          syncObjectsByService: async () => {
            try {
              const currentStore = get();
              const { policyType } = currentStore.template;
              const serviceType =
                PolicyType2Service[policyType as unknown as PolicyType];
              if (serviceType) {
                await PolicyApi.syncTargetObjectsByServiceOnDemand(serviceType);
              }
            } catch (error) {
              window.showNotification({
                titleMsg: t('common.apiException.internalError'),
                severity: 'error',
              });
              console.error(error);
            }
          },
          syncCommunitiesByService: async () => {
            try {
              const currentStore = get();
              const { policyType } = currentStore.template;
              const serviceType =
                PolicyType2Service[policyType as unknown as PolicyType];
              if (serviceType) {
                await PolicyApi.syncTargetCommunitiesByServiceOnDemand(
                  serviceType
                );
              }
            } catch (error) {
              window.showNotification({
                titleMsg: t('common.apiException.internalError'),
                severity: 'error',
              });
              console.error(error);
            }
          },
          syncProfilesByService: async () => {
            try {
              const currentStore = get();
              const { policyType } = currentStore.template;
              const serviceType =
                PolicyType2Service[policyType as unknown as PolicyType];
              if (serviceType) {
                await PolicyApi.syncTargetProfilesByServiceOnDemand(
                  serviceType
                );
              }
            } catch (error) {
              window.showNotification({
                titleMsg: t('common.apiException.internalError'),
                severity: 'error',
              });
              console.error(error);
            }
          },
          searchTargetBase: async (
            keyword: string,
            targetType = TargetType.UserAndGroup,
            pagination?: Targets['pagination'],
            category?: number
          ) => {
            try {
              const currentStore = get();
              const { orgId, setTempVars } = currentStore;
              const { policyType, policySubType } = currentStore.template;
              const serviceType = PolicyServiceMap[policyType];
              if (keyword.trim() === '') {
                // clear search status
                // set to tempVars
                if (targetType === TargetType.Domain) {
                  setTempVars({ searchTargetDomains: [] });
                } else if (targetType === TargetType.Profiles) {
                  setTempVars({ searchTargetProfiles: [] });
                } else if (targetType === TargetType.Objects) {
                  setTempVars({ searchTargetObjects: [] });
                } else {
                  setTempVars({ searchTargetUserGroups: [] });
                }
                // do not use this return value
                return [];
              }
              const resp = await UserApi.searchTarget(
                keyword,
                orgId,
                serviceType,
                targetType,
                pagination,
                category
              );
              const { data } = resp;
              const {
                capacity,
                targets,
                targetGroups,
                pagination: newPagination,
              } = data;
              // empty
              const policyTypeName = getPolicyName(policyType, policySubType);
              let newSearchTargets;
              if (capacity === TargetCapacity.empty) {
                newSearchTargets = [
                  {
                    title: t(`policy.policyTarget.search`, { SEARCH: keyword }),
                    key: 'search',
                    disableCheckbox: true,
                    expanded: true,
                    children: [
                      {
                        title:
                          targetType === TargetType.Domain
                            ? t(`policy.policyTarget.noDomain`)
                            : t(`policy.policyTarget.${policyTypeName}.none`),
                        key: TargetCapacity.empty,
                        disableCheckbox: true,
                      },
                    ],
                  },
                ];
              } else {
                // normal
                const usersData = targets;
                const groupsData = targetGroups;
                const userGroups = convertUserGroup(
                  usersData,
                  groupsData,
                  currentStore.template.policyType
                );
                // add show more node
                const isPagination = !!newPagination;
                const getMore = () =>
                  currentStore.searchTargetBase(
                    keyword,
                    targetType,
                    newPagination
                  );
                const userGroupsWithShowMore = getAllTargetsDisplay(
                  userGroups,
                  isPagination,
                  getMore
                );
                // Only accumulate previous results for pagination ("Show More") requests.
                // For fresh searches, start empty to avoid duplicate entries.
                let searchedTargets: TreeNode[] = [];
                if (
                  pagination &&
                  currentStore.tempVars.searchedTargets &&
                  currentStore.tempVars.searchedTargets.length > 0
                ) {
                  searchedTargets = currentStore.tempVars.searchedTargets;
                }
                const newTargets = [
                  ...searchedTargets,
                  ...userGroupsWithShowMore,
                ];
                setTempVars({
                  searchedTargets: newTargets.filter(
                    (item) => item.key !== 'showMore'
                  ),
                });

                newSearchTargets = [
                  {
                    title: t(`policy.policyTarget.search`, { SEARCH: keyword }),
                    key: 'search',
                    children: newTargets,
                    expanded: true,
                    disableCheckbox: true,
                  },
                ];
                // post handler for teams
                if (policyType === PolicyTypeEnum.Teams) {
                  newSearchTargets = handleAllTargetsForTeams(newSearchTargets);
                }
              }
              // set to tempVars
              if (targetType === TargetType.Domain) {
                setTempVars({ searchTargetDomains: newSearchTargets });
              } else if (targetType === TargetType.Profiles) {
                setTempVars({ searchTargetProfiles: newSearchTargets });
              } else if (targetType === TargetType.Objects) {
                setTempVars({ searchTargetObjects: newSearchTargets });
              } else {
                setTempVars({ searchTargetUserGroups: newSearchTargets });
              }
              return newSearchTargets;
            } catch (error) {
              window.showNotification({
                titleMsg: t('common.apiException.internalError'),
                severity: 'error',
              });
              console.error(error);
              const currentStore = get();
              const { setTempVars } = currentStore;
              const { policyType, policySubType } = currentStore.template;
              const policyTypeName = getPolicyName(policyType, policySubType);
              const newSearchTargets = [
                {
                  title: t(`policy.policyTarget.search`, { SEARCH: keyword }),
                  key: 'search',
                  disableCheckbox: true,
                  expanded: true,
                  children: [
                    {
                      title:
                        targetType === TargetType.Domain
                          ? t(`policy.policyTarget.noDomain`)
                          : t(`policy.policyTarget.${policyTypeName}.none`),
                      key: TargetCapacity.empty,
                      disableCheckbox: true,
                    },
                  ],
                },
              ];
              // set to tempVars
              if (targetType === TargetType.Domain) {
                setTempVars({ searchTargetDomains: newSearchTargets });
              } else if (targetType === TargetType.Profiles) {
                setTempVars({ searchTargetProfiles: newSearchTargets });
              } else if (targetType === TargetType.Objects) {
                setTempVars({ searchTargetObjects: newSearchTargets });
              } else {
                setTempVars({ searchTargetUserGroups: newSearchTargets });
              }
              return newSearchTargets;
            }
          },
          searchHpuTargetByService: async (
            keyword: string,
            targetType = TargetType.HPU,
            pagination?: Targets['pagination']
          ) => {
            const currentStore = get();
            const { searchTargetBase } = currentStore;
            const { policyType } = currentStore.template;
            const serviceType =
              PolicyType2Service[policyType as unknown as PolicyType];
            if (serviceType) {
              return await searchTargetBase(keyword, targetType, pagination);
            }
            return [];
          },
          searchLocalAccountTargetsBase: async (
            keyword: string,
            pagination?: Targets['pagination']
          ) => {
            try {
              const currentStore = get();
              const { orgId, setTempVars } = currentStore;
              if (keyword.trim() === '') {
                // clear search status
                // set to tempVars
                setTempVars({
                  searchTargetUserGroups: [],
                });
                // do not use this return value
                return [];
              }
              const resp = await UserApi.searchLocalAccountTargets(
                keyword,
                orgId,
                pagination
              );
              const { data } = resp;
              const { users, accounts } = data;
              // empty
              let newSearchTargets;
              if (
                users.capacity === TargetCapacity.empty &&
                accounts.capacity === TargetCapacity.empty
              ) {
                newSearchTargets = [
                  {
                    title: t(`policy.policyTarget.search`, { SEARCH: keyword }),
                    key: 'search',
                    disableCheckbox: true,
                    expanded: true,
                    children: [
                      {
                        title: t(`policy.policyTarget.exchange.none`),
                        key: TargetCapacity.empty,
                        disableCheckbox: true,
                      },
                    ],
                  },
                ];
              } else {
                // normal
                const usersData = users.targets;
                const groupsData = users.targetGroups;
                const localUsersData = accounts.targets;
                const userGroups = convertUserGroup(
                  usersData,
                  groupsData,
                  currentStore.template.policyType
                );
                userGroups.unshift({
                  title: t('common.label.allLocalUsers'),
                  key: 'local user',
                  icon: { name: 'user-team', component: <UserTeamIcon /> },
                  children: convertUserGroup(
                    localUsersData,
                    [],
                    currentStore.template.policyType
                  ),
                });
                // add show more node
                const isPagination = !!users.newPagination;
                const getMore = () =>
                  currentStore.searchLocalAccountTargetsBase(
                    keyword,
                    users.newPagination
                  );
                const userGroupsWithShowMore = getAllTargetsDisplay(
                  userGroups,
                  isPagination,
                  getMore
                );
                // add search user node
                newSearchTargets = [
                  {
                    title: t(`policy.policyTarget.search`, { SEARCH: keyword }),
                    key: 'search',
                    children: userGroupsWithShowMore,
                    expanded: true,
                    disableCheckbox: true,
                  },
                ];
              }
              // set to tempVars
              setTempVars({
                searchTargetUserGroups: newSearchTargets,
              });

              return newSearchTargets;
            } catch (error) {
              window.showNotification({
                titleMsg: t('common.apiException.internalError'),
                severity: 'error',
              });
              console.error(error);
            }
            const currentStore = get();
            const { setTempVars } = currentStore;
            const newSearchTargets = [
              {
                title: t(`policy.policyTarget.search`, { SEARCH: keyword }),
                key: 'search',
                disableCheckbox: true,
                expanded: true,
                children: [
                  {
                    title: t(`policy.policyTarget.exchange.none`),
                    key: TargetCapacity.empty,
                    disableCheckbox: true,
                  },
                ],
              },
            ];
            setTempVars({
              searchTargetUserGroups: newSearchTargets,
            });
            return newSearchTargets;
          },
          searchCommunitiesTargetByService: async (
            keyword: string,
            targetType = TargetType.Communities,
            pagination?: Targets['pagination']
          ) => {
            const currentStore = get();
            const { searchTargetBase } = currentStore;
            const { policyType } = currentStore.template;
            const serviceType =
              PolicyType2Service[policyType as unknown as PolicyType];
            if (serviceType) {
              return await searchTargetBase(keyword, targetType, pagination);
            }
            return [];
          },
          searchObjectsTargetByService: async (
            keyword: string,
            targetType = TargetType.Objects,
            pagination?: Targets['pagination']
          ) => {
            const currentStore = get();
            const { searchTargetBase } = currentStore;
            const { policyType, holderType } = currentStore.template;
            const serviceType =
              PolicyType2Service[policyType as unknown as PolicyType];
            if (serviceType) {
              return await searchTargetBase(
                keyword,
                targetType,
                pagination,
                holderType
              );
            }
            return [];
          },
          searchProfilesTargetByService: async (
            keyword: string,
            targetType = TargetType.Profiles,
            pagination?: Targets['pagination']
          ) => {
            const currentStore = get();
            const { searchTargetBase } = currentStore;
            const { policyType } = currentStore.template;
            const serviceType =
              PolicyType2Service[policyType as unknown as PolicyType];
            if (serviceType) {
              return await searchTargetBase(keyword, targetType, pagination);
            }
            return [];
          },
          getMipLabels: async () => {
            try {
              const resp = await UserApi.getMipLabels();
              const { data: mipLabels } = resp;
              // set mipLabels
              if (Array.isArray(mipLabels)) {
                set(
                  produce((state: PolicyStore) => ({
                    ...state,
                    mipLabels,
                  }))
                );
              }
            } catch (error) {
              window.showNotification({
                titleMsg: t('common.apiException.internalError'),
                severity: 'error',
              });
              console.error(error);
            }
          },
          getDlpTemplates: async () => {
            try {
              const resp = await PolicyApi.getDlpTemplates(i18nInst.language);
              const { data: dlpTemplates } = resp;
              // set dlpTemplates
              set(
                produce((state: PolicyStore) => ({
                  ...state,
                  dlpTemplates,
                }))
              );
            } catch (error) {
              window.showNotification({
                titleMsg: t('common.apiException.internalError'),
                severity: 'error',
              });
              console.error(error);
            }
          },
          replaceInvalidAction: (actionDataArr: ActionData[]) => {
            actionDataArr.forEach((actionData) => {
              if (!actionData.actions.includes(actionData.action)) {
                console.log(
                  `Invalid action ${actionData.actionKey}:`,
                  actionData.action
                );
                actionData.handler({
                  [actionData.actionKey]: actionData.actions[0],
                });
              }
            });
          },
        };
      },
      { name: `policyStore-${Date.now()}` }
    )
  );
};
export type PolicyStoreApi = ReturnType<typeof createPolicyStore>;

// export const { useStoreApi } = createZustandContext<PolicyStoreApi>();
